import { ColumnDef } from '@tanstack/react-table';
import { formatDistanceToNow } from 'date-fns';
import { useState } from 'react';
import { Stack } from 'react-bootstrap';
import { Badge } from '../../../components/badge/Badge';
import { BsEnvelope, BsExclamationTriangleFill } from 'react-icons/bs';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { LessonCoverageActions } from '../../../features/dash/views/schedule/sessions/SessionDetail/LessonScheduleActions';
import { OnlineJoinEventIcon } from '../../../features/dash/views/schedule/sessions/SessionDetail/OnlineJoinEventIcon';
import {waitlistStatusColors} from '..';
import { SimpleButton } from '../../../components/buttons/SimpleButton';
import ConfirmationModal from '../../../components/modals/ConfirmationModal';
import { AuditedChangesCell } from '../../../components/tables/cellComponents/AuditedChangesCell';
import { BadgeCell } from '../../../components/tables/cellComponents/BadgeCell';
import CurrencyCell from '../../../components/tables/cellComponents/CurrencyCell';
import { DateCell } from '../../../components/tables/cellComponents/DateCell';
import { EnrollmentCell } from '../../../components/tables/cellComponents/EnrollmentCell';
import { ExpanderCell } from '../../../components/tables/cellComponents/ExpanderCell';
import { InstructorsCell } from '../../../components/tables/cellComponents/InstructorsCell';
import { LessonPlansCell } from '../../../components/tables/cellComponents/LessonPlansCell';
import { LinkCell } from '../../../components/tables/cellComponents/LinkCell';
import { TooltipLinkCell } from '../../../components/tables/cellComponents/TooltipLinkCell';
import WaitlistActionsCell from '../../../components/tables/cellComponents/WaitlistActionsCell';
import { useToast } from '../../../context/ToastContext';
import { lessonAssignmentActionFn } from '../../../features/dash/views/schedule/sessions/SessionDetail';
import { useDeleteExpenseMutation } from '../../../services/endpoints/finance/expense';
import { useDeleteEnrollmentMutation } from '../../../services/endpoints/schedule/enrollment';
import { getCurrentUser } from '../../../services/helper';
import { Audit, Course, InstructorCourseRequest, LessonPlan } from '../../../types/course.type';
import { Lesson } from '../../../types/lesson.type';
import { getLessonInstructorDisplay } from '../../lessonInstructorUtils';
import { SFCOrganization } from '../../../types/place.types';
import {
    expensesCategories,
    expensesContext,
    paymentMethods
} from '../../../utils/consts/finance/finance';
import {
    dateToUTC, displaySessionTimeRange, displaySessionTimeRangeFromFormatted,
    formatDate,
    getCourseSessionTimeDisplay,
    getLessonSessionTimeDisplay, isMissingSessionTime
} from '../../dateUtils';
import { getLessonPlanCellProps } from '../../lessonPlanUtils';
import { getErrorMessage } from '../../utils';

const DeleteEnrollmentButton: React.FC<{ id: number }> = ({ id }) => {
    const [showModal, setShowModal] = useState(false);
    const [deleteEnrollment, { isLoading }] = useDeleteEnrollmentMutation();
    const { addToast } = useToast();

    const handleDelete = async () => {
        try {
            await deleteEnrollment(id).unwrap();
            addToast('Enrollment deleted successfully', 'success');
            setShowModal(false);
        } catch (e) {
            addToast(getErrorMessage(e), 'error');
        }
    };

    return (
        <>
            <button
                className="text-info fw-bolder btn btn-link p-0"
                style={{ fontSize: '.875rem', textDecoration: 'underline' }}
                onClick={() => setShowModal(true)}
            >
                Delete
            </button>
            <ConfirmationModal
                show={showModal}
                title="Delete Enrollment"
                message="Are you sure you want to delete this enrollment?"
                confirmText="Yes, Delete"
                cancelText="Cancel"
                onConfirm={handleDelete}
                onCancel={() => setShowModal(false)}
                isLoading={isLoading}
            />
        </>
    );
};

const DeleteSessionExpenseButton: React.FC<{ id: number }> = ({ id }) => {
    const [showModal, setShowModal] = useState(false);
    const [deleteExpense, { isLoading }] = useDeleteExpenseMutation();
    const { addToast } = useToast();

    const handleDelete = async () => {
        try {
            await deleteExpense(id).unwrap();
            addToast('Expense deleted successfully', 'success');
            setShowModal(false);
        } catch (e) {
            addToast(getErrorMessage(e), 'error');
        }
    };

    return (
        <>
            <button
                className="text-info fw-bolder btn btn-link p-0"
                style={{ fontSize: '.875rem', textDecoration: 'underline' }}
                onClick={() => setShowModal(true)}
            >
                Delete
            </button>
            <ConfirmationModal
                show={showModal}
                title="Delete Expense"
                message="Are you sure you want to delete this expense?"
                confirmText="Yes, Delete"
                cancelText="Cancel"
                onConfirm={handleDelete}
                onCancel={() => setShowModal(false)}
                isLoading={isLoading}
            />
        </>
    );
};

const currentUser = getCurrentUser();
const isAdminUser = currentUser?.isAdmin;

export const sessionColumns: ColumnDef<Course>[] = [
    {
        id: 'expander',
        header: () => null,
        cell: ({ row }) =>
            row.original?.lessons && row.original?.lessons?.length > 0 ? <ExpanderCell row={row} /> : null
    },
    {
        header: 'ID',
        accessorKey: 'id',
        cell: ({ getValue, row }) => (
            <LinkCell
                content={getValue<string>()}
                url={`/admin/schedule/sessions/${row.original.id}`}
            />
        )
    },
    {
        header: 'Session',
        accessorKey: 'title',
        cell: ({ getValue, row }) => (
            <LinkCell
                content={getValue<string>()}
                url={`/admin/schedule/sessions/${row.original.id}`}
            />
        )
    },
    {
        header: 'Start Date',
        accessorKey: 'starts_on',
        cell: ({ row }) => {
            if (!row.original.starts_on) {
                return 'Invalid date';
            }
            return (
                <DateCell
                    date={row.original.starts_on}
                    format={'MM/dd/yyyy'}
                />
            );
        }
    },
    {
        header: 'End Date',
        accessorKey: 'ends_on',
        cell: ({ row }) => {
            if (!row.original.ends_on) {
                return 'Invalid date';
            }
            return (
                <DateCell
                    date={row.original.ends_on}
                    format={'MM/dd/yyyy'}
                />
            );
        }
    }
];

export const sessionOtherColumns: ColumnDef<Course>[] = [
    {
        header: 'Instructor',
        accessorKey: 'course_assignment.instructor_id',
        cell: ({ row }) => {
            if (!row.original?.course_assignments?.length) return;
            const { instructor } = row.original?.course_assignments?.[0];
            return (
                <span className="text-info fw-bolder text-truncate">
          {`${instructor?.first_name} ${instructor?.last_name}`}
        </span>
            );
        }
    },
    {
        header: 'Day of Week',
        accessorKey: 'day_of_week',
        cell: ({ row }) => <span>{row.original.day_of_week}</span>
    },
    {
        header: 'Time',
        accessorKey: 'formatted_time',
        cell: ({ row }) => {
            const timeDisplay = displaySessionTimeRangeFromFormatted(
                row.original.formatted_time,
                (row.original.venue as any)?.location?.timezone
            );
            if (!timeDisplay) return 'Invalid times';
            return <span className="text-nowrap">{timeDisplay}</span>;
        }
    },
    {
        header: 'Enrollment',
        accessorKey: 'enrollment',
        cell: ({ getValue, row }) => (
            <EnrollmentCell
                type={row.original.payment_type}
                value={getValue<string>()}
            />
        )
    },
    {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ getValue }) => (
            <BadgeCell
                active={getValue<string>() === 'open'}
                activePhrase="Open"
                inactivePhrase={getValue<string>() === 'offline' ? 'Offline' : 'Cancelled'}
            />
        )
    }
];

export const sessionInvoiceColumns: ColumnDef<Course>[] = [
    {
        header: 'Amount Receivable',
        accessorKey: 'sum_enrollment_values',
        cell: ({ getValue }) => <CurrencyCell value={getValue<string>()} />
    },
    {
        header: 'Amount Invoiced',
        accessorKey: 'sum_invoice_item_totals',
        cell: ({ getValue }) => <CurrencyCell value={getValue<string>()} />
    },
    {
        header: 'Organizational\nInvoicing Contacts',
        accessorKey: 'venue.organizations',
        cell: ({ getValue }) => {
            let orgs = getValue<SFCOrganization[]>();
            let cells = orgs?.map((o) => (
                <TooltipLinkCell
                    tooltip={`${o.contacts?.length} contacts should be sent invoices.`}
                    key={o.id}
                    url={`/admin/places/organizations/${o.id}`}
                    content={o.name}
                />
            ));
            return <>{cells}</>;
        }
    }
];

export const simpleScheduleColumns: ColumnDef<Lesson>[] = [
    {
        header: 'Class Date',
        id: 'lesson_date',
        accessorKey: 'date',
        cell: ({ getValue }) =>
            getValue<string>() ? (
                <DateCell date={getValue<string>()} format="MM/dd/yyyy" />
            ) : (
                'Invalid date'
            )
    },
    {
        header: 'Day Of Week',
        id: 'lesson_day_of_the_week',
        accessorKey: 'date',
        cell: ({ getValue, row }) =>
            getValue<string>() ? (
                <DateCell
                    date={getValue<string>()}
                    format="EEEE"
                />
            ) : (
                'Invalid date'
            )
    },
    {
        header: 'Time',
        id: 'lesson_time',
        cell: ({ row }) => {
            const timeDisplay = displaySessionTimeRange(
                isMissingSessionTime(row.original.starts_at)
                    ? row.original.course?.starts_at
                    : row.original.starts_at,
                isMissingSessionTime(row.original.ends_at)
                    ? row.original.course?.ends_at
                    : row.original.ends_at,
                (row.original.course?.venue as any)?.location?.timezone,
            );
            if (!timeDisplay) return 'Invalid date';
            return <span className="text-nowrap">{timeDisplay}</span>;
        }
    },
    // {
    //     header: '',
    //     accessorKey: 'id',
    //     id: 'Actions',
    //     cell: ({ getValue }) => {
    //         // eslint-disable-next-line react-hooks/rules-of-hooks
    //         const navigate = useNavigate();
    //         return getValue<number>() ? (
    //             <SimpleButton
    //                 variant="primary"
    //                 height={28}
    //                 className="pt-0"
    //                 onClick={() => navigate(`/admin/schedule/classes/${getValue<number>()}`)}
    //             >
    //                 <Stack direction="horizontal" gap={1}>
    //                     <span style={{ fontSize: '.875rem' }}>See Class</span>
    //                     <span className="pb-1">
    //           <FaArrowRight size={14} />
    //         </span>
    //                 </Stack>
    //             </SimpleButton>
    //         ) : (
    //             <></>
    //         );
    //     },
    //     enableSorting: false
    // }
];

export const getScheduleColumns: (fn: lessonAssignmentActionFn) => ColumnDef<Lesson>[] = (fn) => [
    {
        header: 'Number',
        accessorKey: 'id',
        cell: ({ row }) => row.index + 1
    },
    {
        header: 'Date',
        accessorKey: 'date',
        cell: ({ getValue, row }) => <DateCell date={getValue<string>()} />
    },
    {
        header: 'Day',
        id: 'day',
        accessorKey: 'date',
        cell: ({ getValue }) => (
            <DateCell
                date={getValue<string>()}
                format="EEEE"
            />
        )
    },
    {
        header: 'Time',
        id: 'formatted_time',
        cell: ({ row }) => {
            const timeDisplay = getLessonSessionTimeDisplay(row.original, row.original.course);
            if (!timeDisplay) return null;

            return (
                <span className="text-nowrap d-inline-flex align-items-center gap-2">
                    <span>{timeDisplay}</span>
                    <OnlineJoinEventIcon
                        course={row.original.course}
                        lessonDate={row.original.date}
                        lessonId={row.original.id}
                    />
                </span>
            );
        }
    },
    {
        header: 'Class Notes',
        id: 'class_notes',
        accessorKey: 'notes'
    },
    {
        header: 'Lesson Plans',
        accessorKey: 'lesson_plans',
        cell: ({ row, getValue }) => (
            <LessonPlansCell
                {...getLessonPlanCellProps(
                    getValue<LessonPlan[]>(),
                    (row.original as any).virtual_lesson_plan ?? null
                )}
            />
        )
    },
    {
        header: 'Instructors',
        accessorKey: 'lesson_assignments',
        cell: ({ row }) => (
            <InstructorsCell
                instructors={getLessonInstructorDisplay(row.original, row.original.course)}
                lessonAssignments={row.original.lesson_assignments}
            />
        )
    },
    {
        header: 'Coverage',
        id: 'coverage',
        enableSorting: false,
        cell: ({ row }) => (
            <LessonCoverageActions
                lesson={row.original}
                course={row.original.course}
                actionCallback={fn}
            />
        )
    }
];

export const getRosterColumns: (
    onSendWelcomeEmail?: (email: string) => void
) => ColumnDef<any>[] = (onSendWelcomeEmail) => [
    {
        header: 'Number',
        id: 'number',
        enableSorting: false,
        cell: ({ row }) => row.index + 1
    },
    {
        header: 'First Name',
        accessorKey: 'first_name',
        cell: ({ getValue, row }) => (
            <LinkCell
                content={getValue<string>()}
                url={`/admin/sfc-users/children/${row.original.id}`}
            />
        )
    },
    {
        header: 'Last Name',
        accessorKey: 'last_name',
        cell: ({ getValue, row }) => (
            <LinkCell
                content={getValue<string>()}
                url={`/admin/sfc-users/children/${row.original.id}`}
            />
        )
    },
    {
        header: `Allergy / Dietary\nRestrictions`,
        accessorKey: 'allergies',
        enableSorting: false,
        cell: ({ getValue, row }) => {
            const allergies = getValue<
                { id: number; allergy: string; severity?: string; details?: string }[]
            >();
            if (!allergies || allergies.length === 0) return null;
            return (
                <Stack direction="vertical" gap={1}>
                    {allergies.map((a) => {
                        const severity = (a.severity ?? '').trim();
                        const details = (a.details ?? '').trim();
                        const tooltip =
                            severity && details
                                ? `${severity} - ${details}`
                                : severity || details || 'Allergy / dietary restriction';
                        return (
                            <Stack
                                key={a.id}
                                direction="horizontal"
                                gap={1}
                                className="align-items-center text-danger"
                            >
                                <BsExclamationTriangleFill
                                    size={14}
                                    className="text-danger"
                                    style={{ flexShrink: 0 }}
                                    title={tooltip}
                                />
                                <LinkCell
                                    content={a.allergy}
                                    url={`/admin/allergies/${a.id}`}
                                    underline
                                    className="text-danger"
                                    state={{
                                        returnTo: `/admin/schedule/sessions/${row.original.course.id}`
                                    }}
                                />
                            </Stack>
                        );
                    })}
                </Stack>
            );
        }
    },
    {
        header: 'Social / Emotional\nNeeds',
        accessorKey: 'social_emotional_needs',
        enableSorting: false,
        cell: ({ row }) => {
            const social = (row.original.social_emotional_needs ?? '').trim();
            const legacy = (row.original.emotional_needs ?? '').trim();
            return social || legacy || null;
        }
    },
    {
        header: 'Parent Name',
        accessorKey: 'parent_first_name',
        cell: ({ row }) => (
            <LinkCell
                content={`${row.original.parent.first_name} ${row.original.parent.last_name}`}
                url={`/admin/sfc-users/parents/${row.original.parent.id}`}
            />
        ),
        enableSorting: false
    },

    {
        header: 'Parent Email',
        accessorKey: 'parent_email',
        cell: ({ row }) => (
            <Stack
                direction="horizontal"
                gap={2}
            >
                <LinkCell
                    content={`${row.original.parent.email}`}
                    url={`/admin/sfc-users/parents/${row.original.parent.id}`}
                />
                {onSendWelcomeEmail && isAdminUser && (
                    <BsEnvelope
                        size={16}
                        className="text-info cursor-pointer"
                        style={{ cursor: 'pointer', flexShrink: 0 }}
                        title="Send welcome email"
                        onClick={() => onSendWelcomeEmail(row.original.parent.email)}
                    />
                )}
            </Stack>
        ),
        enableSorting: false
    }
];

export const rosterColumns = getRosterColumns();

export const enrollmentColumns: ColumnDef<any>[] = [
    {
        id: 'number_of_students_expected',
        header: 'Number of Students Expected',
        accessorKey: 'quantity'
    },
    {
        id: 'tuition',
        header: 'Tuition (per student, per class)',
        accessorKey: 'tuition',
        cell: ({ getValue }) => <CurrencyCell value={getValue<string>()} />
    },
    {
        id: 'number_of_classes',
        header: 'Number of Classes',
        accessorKey: 'number_of_classes'
    },
    {
        id: 'scholarship',
        header: 'Scholarship',
        accessorKey: 'scholarship.name'
    },
    {
        id: 'notes',
        header: 'Notes',
        accessorKey: 'notes',
        enableSorting: false
    },
    {
        id: 'invoice_notes',
        header: 'Invoice Notes',
        accessorKey: 'invoice_notes'
    },
    {
        id: 'total',
        header: 'Total',
        accessorKey: 'value',
        cell: ({ getValue }) => (
            <>
                $
                {parseFloat(getValue() as string).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                })}
            </>
        )
    },
    {
        id: 'actions',
        header: () => null,
        enableSorting: false,
        cell: ({ row }) => (
            <Stack
                direction="horizontal"
                gap={2}
            >
                <LinkCell
                    content="Edit"
                    url={`/admin/enrollments/${row.original.id}/edit`}
                    underline
                />
                <DeleteEnrollmentButton id={row.original.id} />
            </Stack>
        )
    }
];

export const flatRateenrollmentColumns: ColumnDef<any>[] = [
    {
        id: 'number_of_students_expected',
        header: 'Number of Students Expected',
        accessorKey: 'quantity'
    },
    {
        id: 'tuition',
        header: 'Flat Rate Tuition (per class)',
        accessorKey: 'tuition',
        cell: ({ getValue }) => <CurrencyCell value={getValue<string>()} />
    },
    {
        id: 'number_of_classes',
        header: 'Number of Classes',
        accessorKey: 'number_of_classes'
    },
    {
        id: 'scholarship',
        header: 'Scholarship',
        accessorKey: 'scholarship.name'
    },
    {
        id: 'notes',
        header: 'Notes',
        accessorKey: 'notes',
        enableSorting: false
    },
    {
        id: 'invoice_notes',
        header: 'Invoice Notes',
        accessorKey: 'invoice_notes'
    },
    {
        id: 'total',
        header: 'Total',
        accessorKey: 'value',
        cell: ({ getValue }) => (
            <>
                $
                {parseFloat(getValue() as string).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                })}
            </>
        )
    },
    {
        id: 'actions',
        header: () => null,
        enableSorting: false,
        cell: ({ row }) => (
            <Stack
                direction="horizontal"
                gap={2}
            >
                <LinkCell
                    content="Edit"
                    url={`/admin/enrollments/${row.original.id}/edit`}
                    underline
                />
                <DeleteEnrollmentButton id={row.original.id} />
            </Stack>
        )
    }
];

export const lessonNotesColumns: ColumnDef<any>[] = [
    {
        header: 'Notes',
        accessorKey: 'notes',
        enableSorting: false
    }
];

export const waitlistColumns: ColumnDef<any>[] = [
    ...rosterColumns,
    {
        header: 'Status',
        id: 'waitlist_status',
        enableSorting: false,
        cell: ({ row }) => {
            const status = row.original.waitlists?.status ?? '';
            const color = waitlistStatusColors[status] ?? '#5e6469';
            return (
                <span
                    style={{
                        backgroundColor: color,
                        color: '#fff',
                        fontSize: '.75rem',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        textTransform: 'capitalize',
                        display: 'inline-block'
                    }}
                >
            {status}
        </span>
            );
        }
    },
    {
        header: 'Last Action',
        id: 'waitlist_updated_at',
        enableSorting: false,
        cell: ({ row }) => {
            const ts = row.original.waitlists?.updatedAt ?? row.original.waitlists?.updated_at;
            if (!ts) return <></>;
            return (
                <span className="text-nowrap">
                    {formatDistanceToNow(new Date(ts), { addSuffix: true })}
                </span>
            );
        }
    },
    {
        header: () => null,
        id: 'actions',
        enableSorting: false,
        cell: ({ row }) => <WaitlistActionsCell row={row} />
    }
];

export const waiversColumns: ColumnDef<any>[] = [
    {
        header: 'Child First Name',
        accessorKey: 'child_first_name'
    },
    {
        header: 'Child Last Name',
        accessorKey: 'child_last_name'
    },
    {
        header: 'Parent First Name',
        accessorKey: 'parent_first_name'
    },
    {
        header: 'Parent Last Name',
        accessorKey: 'parent_last_name'
    },
    {
        header: 'Parent Email Address',
        accessorKey: 'parent_email_address'
    },
    {
        header: 'Parent Mobile Number',
        accessorKey: 'parent_mobile_number'
    }
];

export const sessionExpensesColumns: ColumnDef<any>[] = [
    {
        header: 'Category',
        accessorKey: 'category',
        enableSorting: false,
        cell: ({ getValue }) => <>{expensesCategories[getValue() as string]}</>
    },
    {
        header: 'Amount',
        accessorKey: 'amount',
        enableSorting: false,
        cell: ({ getValue }) => <CurrencyCell value={getValue<string>()} />
    },
    {
        header: 'Context',
        accessorKey: 'context',
        enableSorting: false,
        cell: ({ getValue }) => <>{expensesContext[getValue() as string]}</>
    },
    {
        header: `Due On`,
        accessorKey: 'due_on',
        enableSorting: false,
        cell: ({ getValue }) => {
            const value = getValue<string>();
            const date = new Date(value);
            if (isNaN(date.getTime())) {
                return <></>;
            }
            return date ? dateToUTC(date, 'MMMM d, yyyy') : '';
        }
    },
    {
        header: 'Paid On',
        accessorKey: 'paid_on',
        enableSorting: false
    },
    {
        header: 'Notes',
        accessorKey: 'notes',
        enableSorting: false
    },
    {
        header: 'Invoice number',
        accessorKey: 'invoice_number',
        enableSorting: false
    },
    {
        header: 'Payment Method',
        accessorKey: 'payment_method',
        enableSorting: false,
        cell: ({ getValue }) => <>{paymentMethods[getValue() as string]}</>
    },
    {
        header: 'Total',
        accessorKey: 'total_for_session',
        enableSorting: false,
        cell: ({ getValue }) => <CurrencyCell value={getValue<string>()} />
    },
    {
        id: 'actions',
        header: () => null,
        enableSorting: false,
        cell: ({ row }) => (
            <Stack
                direction="horizontal"
                gap={2}
            >
                <LinkCell
                    content="Edit"
                    url={`/admin/finance/expenses/${row.original.id}/edit`}
                    underline
                />
                {row.original.paid_on === null && <DeleteSessionExpenseButton id={row.original.id} />}
            </Stack>
        )
    }
];

export const revisionsColumns: ColumnDef<Audit>[] = [
    {
        header: 'User',
        accessorKey: 'username',
        cell: ({ getValue }) => (
            <span className="text-truncate">
        {getValue<string>() ? getValue<string>() : 'Not specified'}
      </span>
        )
    },
    {
        header: 'Action',
        accessorKey: 'action',
        cell: ({ getValue }) => <span className="text-capitalize">{getValue<string>()}</span>
    },
    {
        header: 'Changes',
        accessorKey: 'audited_changes',
        cell: ({ getValue }) => <AuditedChangesCell changes={getValue<string>()} />
    },
    {
        header: `Comment`,
        accessorKey: 'comment'
    },
    {
        header: 'Time',
        accessorKey: 'created_at',
        cell: ({ getValue }) => {
            const value = getValue<string | null>();
            if (!value) return <span>—</span>;
            return <span>{formatDate(new Date(value), 'MM/dd/yyyy h:mma zzz')}</span>;
        }
    }
];

export const needsInstructorColumns: ColumnDef<Course>[] = [
    {
        header: 'ID',
        accessorKey: 'id',
        cell: ({ getValue, row }) => (
            <LinkCell
                content={getValue<string>()}
                url={`/admin/schedule/sessions/${row.original.id}`}
            />
        )
    },
    {
        header: 'Session',
        accessorKey: 'title',
        cell: ({ getValue, row }) => (
            <LinkCell
                content={getValue<string>()}
                url={`/admin/schedule/sessions/${row.original.id}`}
            />
        )
    },
    {
        header: 'Venue',
        accessorKey: 'location',
        cell: ({ row }) => (
            <LinkCell
                content={row.original.venue.title}
                url={`/admin/places/venues/${row.original.venue.id}`}
            />
        )
    },
    {
        header: 'Map',
        accessorKey: 'map',
        cell: ({ row }) => (
            <TooltipLinkCell
                tooltip={`${row.original.venue.address}, ${row.original.venue.city}, ${row.original.venue.state}, ${row.original.venue.zip}`}
                content={<FaMapMarkerAlt />}
                url=""
                onClick={() => {
                    window.open(
                        `https://www.google.com/maps?q=${encodeURIComponent(row.original.venue.address)}`,
                        '_blank'
                    );
                }}
                underline
            />
        )
    },
    {
        header: 'Dates',
        accessorKey: 'starts_on',
        cell: ({ row }) => {
            if (!row.original.starts_on || !row.original.ends_on) {
                return 'Invalid dates';
            }
            return (
                <>
                    <span className="text-truncate">
                        <DateCell
                            date={row.original.starts_on}
                            format="MM/dd/yyyy"
                        />
                        {' - '}
                        <DateCell
                            date={row.original.ends_on}
                            format="MM/dd/yyyy"
                        />
                    </span>
                    {row.original.day_of_week && (
                        <>
                            <br />
                            <span className="fw-light ff-inter">{row.original.day_of_week}</span>
                        </>
                    )}
                </>
            );
        }
    },
    {
        header: 'Time',
        accessorKey: 'formatted_time',
        cell: ({ row }) => {
            const timeDisplay = getCourseSessionTimeDisplay(row.original);
            if (!timeDisplay) return 'Invalid times';
            return <span className="text-nowrap">{timeDisplay}</span>;
        }
    },
    {
        header: 'Day of Week',
        accessorKey: 'day_of_week'
    },
    {
        header: 'Enrollment',
        accessorKey: 'enrollment',
        cell: ({ getValue, row }) => (
            <EnrollmentCell
                type={row.original.payment_type}
                value={getValue<string>()}
            />
        )
    },
    {
        header: 'Scheduled Instructors',
        accessorKey: 'scheduled_instructors'
    }
];

export const instructorSessionRequestsColumns: ColumnDef<InstructorCourseRequest>[] = [
    {
        header: 'ID',
        accessorKey: 'id'
    },
    {
        header: 'Instructor',
        id: 'lesson_instructor',
        cell: ({ row }) => (
            <LinkCell
                content={row.original.instructor.full_name}
                url={``}
            />
        )
    },
    {
        header: 'Session',
        id: 'session_title',
        accessorKey: 'course.title',
        cell: ({ getValue, row }) => (
            <LinkCell
                content={getValue<string>()}
                url={`/admin/schedule/sessions/${row.original.course_id}`}
            />
        )
    },
    {
        header: 'Request Date',
        id: 'request_date',
        accessorKey: 'created_at',
        cell: ({ row }) => formatDate(new Date(row.original.created_at), 'MM/dd/yyyy h:mm zzz')
    }
];
